import React, { useState } from 'react';

interface Props {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
}

interface UserAttributes {
    hadTrial: boolean;
    readOnly: boolean;
    disabled: boolean;
    deletedDate: string | null;
}

const DEFAULT_ATTRS: UserAttributes = { hadTrial: false, readOnly: false, disabled: false, deletedDate: null };

export const AccountManagementSection: React.FC<Props> = ({ API_BASE, authHeaders }) => {
    const [username, setUsername] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [attributes, setAttributes] = useState<UserAttributes>(DEFAULT_ATTRS);
    const [draft, setDraft] = useState<UserAttributes>(DEFAULT_ATTRS);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hardDeleting, setHardDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const dirty =
        loaded &&
        (draft.hadTrial !== attributes.hadTrial ||
            draft.readOnly !== attributes.readOnly ||
            draft.disabled !== attributes.disabled ||
            draft.deletedDate !== attributes.deletedDate);

    const handleRead = async () => {
        if (!username.trim()) return;
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${API_BASE}/profile/get-user-attributes`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ username: username.trim() }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw new Error(data?.message || 'Failed to read user attributes.');
            }

            const attrs: UserAttributes = {
                hadTrial: data.hadTrial === true,
                readOnly: data.readOnly === true,
                disabled: data.disabled === true,
                deletedDate: data.deletedDate || null,
            };

            setAttributes(attrs);
            setDraft(attrs);
            setLoaded(true);
        } catch (err: any) {
            setError(err.message || 'Failed to read user attributes.');
            setLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${API_BASE}/profile/set-user-attributes`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ username: username.trim(), ...draft }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw new Error(data?.message || 'Failed to save user attributes.');
            }

            setAttributes(draft);
            setSuccessMessage(data.message || 'Attributes saved.');
        } catch (err: any) {
            setError(err.message || 'Failed to save user attributes.');
        } finally {
            setSaving(false);
        }
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUsername(e.target.value);
        setLoaded(false);
        setAttributes(DEFAULT_ATTRS);
        setDraft(DEFAULT_ATTRS);
        setError(null);
        setSuccessMessage(null);
    };

    const handleCheckbox = (key: Exclude<keyof UserAttributes, 'deletedDate'>) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setDraft(prev => ({ ...prev, [key]: e.target.checked }));
        setSuccessMessage(null);
    };

    const handleClearDeletedDate = () => {
        setDraft(prev => ({ ...prev, deletedDate: null }));
        setSuccessMessage(null);
    };

    const handleHardDelete = async () => {
        const confirmed = window.confirm(
            `PERMANENTLY DELETE @${username.trim().replace(/^@/, '')}?\n\nThis will immediately trigger a full hard delete — all data, media, and the Cognito account will be removed. This cannot be undone.`
        );
        if (!confirmed) return;

        setHardDeleting(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${API_BASE}/profile/hard-delete-account`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ username: username.trim() }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw new Error(data?.message || 'Failed to queue hard delete.');
            }

            setLoaded(false);
            setAttributes(DEFAULT_ATTRS);
            setDraft(DEFAULT_ATTRS);
            setUsername('');
            setSuccessMessage(data.message || 'Hard delete queued.');
        } catch (err: any) {
            setError(err.message || 'Failed to queue hard delete.');
        } finally {
            setHardDeleting(false);
        }
    };

    return (
        <section className="dashboard-card">
            <h2>Account Management</h2>
            <div className="form-group">
                <label htmlFor="acct-mgmt-username">Username (handle)</label>
                <input
                    id="acct-mgmt-username"
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    className="dashboard-input"
                    placeholder="@username"
                />
            </div>
            <button
                onClick={handleRead}
                disabled={!username.trim() || loading}
                className="btn btn-primary"
                style={{ marginBottom: '1.25rem' }}
            >
                {loading ? 'Reading...' : 'Read'}
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
                {(
                    [
                        { key: 'hadTrial', label: 'Had Free Trial' },
                        { key: 'readOnly', label: 'Read Only' },
                        { key: 'disabled', label: 'Disabled' },
                    ] as { key: Exclude<keyof UserAttributes, 'deletedDate'>; label: string }[]
                ).map(({ key, label }) => (
                    <label
                        key={key}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: loaded ? undefined : '#aaa',
                            cursor: loaded ? 'pointer' : 'not-allowed',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={draft[key]}
                            onChange={handleCheckbox(key)}
                            disabled={!loaded}
                        />
                        {label}
                    </label>
                ))}

                <div style={{ marginTop: '0.4rem', padding: '0.75rem', background: '#f9f0f0', borderRadius: '6px', border: '1px solid #e8c8c8' }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '0.4rem', color: '#b00020' }}>
                        Deleted Date
                    </div>
                    {loaded ? (
                        draft.deletedDate ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '13px', color: '#333' }}>
                                    {new Date(draft.deletedDate).toLocaleString()}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleClearDeletedDate}
                                    className="btn btn-primary"
                                    style={{ fontSize: '12px', padding: '4px 10px' }}
                                >
                                    Clear (restore account)
                                </button>
                            </div>
                        ) : (
                            <span style={{ fontSize: '13px', color: '#666' }}>Not set — account is active</span>
                        )
                    ) : (
                        <span style={{ fontSize: '13px', color: '#aaa' }}>Read a user first</span>
                    )}
                </div>
            </div>

            <button
                onClick={handleSave}
                disabled={!dirty || saving}
                className="btn btn-primary"
            >
                {saving ? 'Saving...' : 'Save'}
            </button>

            <div style={{ marginTop: '1.5rem', padding: '0.75rem', background: '#fff0f0', borderRadius: '6px', border: '1px solid #e8c8c8' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '0.5rem', color: '#b00020' }}>
                    Danger Zone
                </div>
                <button
                    onClick={handleHardDelete}
                    disabled={!loaded || hardDeleting}
                    className="btn"
                    style={{ background: '#b00020', color: '#fff', border: 'none', cursor: loaded ? 'pointer' : 'not-allowed', opacity: loaded ? 1 : 0.5 }}
                >
                    {hardDeleting ? 'Queuing...' : 'Hard Delete Account'}
                </button>
                <p style={{ margin: '0.4rem 0 0', fontSize: '12px', color: '#666' }}>
                    Immediately triggers a full permanent deletion of all user data, media, and their Cognito account.
                </p>
            </div>

            {error && <p className="status status-error">{error}</p>}
            {successMessage && <p className="status">{successMessage}</p>}
        </section>
    );
};
